import {Show, useDataProvider} from "react-admin";
import { Typography } from "@material-ui/core";
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import BackButton from "../../components/button/backButton";
import LoadingView from "../transition/loadingView";
import GenericError from "../../components/error/genericError";
import CustomerDetailsCard from "../../components/card/customerDetailsCard";
import {useEffect, useRef, useState} from "react";
import VerificationDetailsCard from "../../components/card/verificationDetailsCard"
import CustomerTitleCard from "../../components/card/customerTitleCard";
import CustomerBalanceCard from "../../components/card/customerBalanceCard";
import NotesCard from "../../components/card/notesCard";
import CustomerSectionTabs from "../../components/navbar/customerSectionTabs";
import ActivityList from "../../components/list/activityList";
import PaymentMethods from "../../components/section/paymentMethods";
import SettingsSection from "../../components/section/settingsSection";
import CardsSection from "../../components/section/cardsSection";
import ComplianceDetailsCard from "../../components/card/complianceDetailsCard";

const CUSTOMER_CURRENCY = "USD";

const CustomerShow = (props) => {
    const useStyles = makeStyles((theme) => ({
        root: {
            width: '100%'
        },
        titleContainer: {
            marginTop: theme.spacing(6),
            marginBottom: theme.spacing(3)
        },
        gridContainer: {
            alignItems: 'stretch',
        },
        linkedHeader: {
            height: 18,
            opacity: 0.5,
            marginTop: 23,
            marginLeft: 16,
            marginBottom: 23,
            fontFamily: 'DM Sans',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: 14,
            lineHeight: '100%',
            letterSpacing: 0,
        },
        centerColumn: {
            display: 'flex',
            flexDirection: 'column',
        },
        rightColumn: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        },
        fillSection: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            minHeight: 0,
        },
        fillCard: {
            flexGrow: 1,
            display: 'flex',
            minHeight: 0,
            '& > *': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                flex: 1
            }
        }
    }));
    const classes = useStyles();
    const activityList = useRef(null);
    const transactionList = useRef(null);
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState("activity");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();

    const dataProvider = useDataProvider();
    const [triggerRefresh, setTriggerRefresh] = useState(false);

    useEffect(() => {
        if (triggerRefresh) {
            setLoading(true);
            dataProvider.getOne('customers', {id: props.id})
                .then(({data}) => {
                    setData(data);
                    setError(null)
                })
                .catch((error) => {
                    setError(error);
                })
                .finally(() => {
                    setLoading(false);
                    setTriggerRefresh(false);
                    // Refresh activity list immediately (BFF fetches fresh OTC orders from account API)
                    activityList?.current?.refresh();
                });
        }
    }, [triggerRefresh]);

    useEffect(() => {
        dataProvider.getOne('customers', {id: props.id})
            .then(({data}) => {
                setData(data);
                setError(null)
            })
            .catch((error) => {
                setError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    if (error) return <><BackButton/><GenericError/></>;
    if (loading) return <LoadingView variant="show"/>;

    const {
        name,
        id,
        accounts,
        status,
        kycStatus
    } = data;
    const {
        id: accountId,
        balances,
        otcBalances
    } = accounts?.[0] || {wallet: {}};
    const {[CUSTOMER_CURRENCY]: customerBalances} = balances || {[CUSTOMER_CURRENCY]: 0};
    // Use onchainAvailableBalance to show only on-chain balance (excluding OTC)
    // Fall back to availableBalance if onchainAvailableBalance is not set
    const onchainBalance = customerBalances.onchainAvailableBalance ?? customerBalances.availableBalance ?? 0
    
    // Calculate OTC USD balance
    const otcUsdBalance = otcBalances?.find(balance => balance.currency === 'USD')?.availableBalance ?? 0
    
    // Calculate combined balance (both on-chain and OTC are in USD)
    const combinedBalance = onchainBalance + otcUsdBalance
    
    const totalInterest = customerBalances.totalInterestAccrued ?? 0
    return (
        <Show className={console.root} {...props}>
            <>
                <BackButton/>
                <div className={classes.titleContainer}>
                    <CustomerTitleCard customerLocked={status == "SUSPENDED"} customerClosed={status == "CLOSED"}
                                       kycStatus={kycStatus} name={name} customerId={id}
                                       onCustomerUpdate={() => setTriggerRefresh(true)}/>
                </div>
                <Grid container spacing={3} className={classes.gridContainer}>
                    <Grid item xl={3} lg={3} md={3} sm={12} xs={12}>
                        <CustomerDetailsCard data={data} refreshCustomerView={() => setTriggerRefresh(true)}/>
                        <VerificationDetailsCard customerData={data}/>
                    </Grid>
                    <Grid item xl={6} lg={6} md={6} sm={12} xs={12} className={classes.centerColumn}>
                        <CustomerBalanceCard
                            accountId={accountId}
                            customerBalance={combinedBalance}
                            onchainBalance={onchainBalance}
                            otcBalance={otcUsdBalance}
                            totalInterest={totalInterest}
                            activityListRef={activityList}
                            customerId={id}
                            onRefreshCustomer={() => setTriggerRefresh(true)}
                        />
                        <CustomerSectionTabs value={activeTab} onChange={setActiveTab}/>
                        <div className={classes.fillSection}>
                            {activeTab === "activity" && (
                                <div className={classes.fillCard}>
                                    <ActivityList
                                        ref={activityList}
                                        customerId={id}
                                        accountId={accountId}
                                        onRefreshCustomer={() => setTriggerRefresh(true)}
                                    />
                                </div>
                            )}
                            {activeTab === "payment_methods" && (
                                <div className={classes.fillCard}>
                                    <PaymentMethods customerId={id} initialMode={'crypto'} />
                                </div>
                            )}
                            {activeTab === "linked_accounts" && (
                                <>
                                    <Typography variant="h6" className={classes.linkedHeader}>Linked accounts</Typography>
                                    <div className={classes.fillCard}>
                                        <PaymentMethods customerId={id} initialMode={'fiat'} useLinkedAccounts={true} showPills={false} />
                                    </div>
                                </>
                            )}
                            {activeTab === "settings" && (
                                <div className={classes.fillCard}>
                                    <SettingsSection customerId={id} onUnhideVault={async () => {
                                        try {
                                            await dataProvider.create('unhideVault', {customerId: id});
                                        } catch (e) { /* noop */ }
                                    }} onUpdated={() => setTriggerRefresh(true)} />
                                </div>
                            )}
                            {activeTab === "cards" && (
                                <div className={classes.fillCard}>
                                    <CardsSection customerId={id} />
                                </div>
                            )}
                            {activeTab === "compliance" && (
                                <div>
                                    <ComplianceDetailsCard customerId={id} />
                                </div>
                            )}
                        </div>
                    </Grid>
                    <Grid item xl={3} lg={3} md={3} sm={12} xs={12} className={classes.rightColumn}>
                        <NotesCard notes={data.notes} customerId={id}
                                   refreshCustomerView={() => setTriggerRefresh(true)}/>
                    </Grid>
                </Grid>
            </>
        </Show>
    );
};

export default CustomerShow;